// ./src/components/features/VolunteerSlots/VolunteerRoleSelect/VolunteerRoleSelect.tsx
//
// Picks a reusable account-level volunteer role for a slot, with in-place create
// (wraps its own VolunteerRolesProvider so the create drawer shares the list —
// same idea as TaskLocationFields' venue picker).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "@mantine/core";
import { STATUSES } from "@sdflc/utils";

import { DEFAULT_PAGE_SIZE } from "@/config";
import { VolunteerRole, VolunteerRoleService } from "@/utils/api";
import { VolunteerRolesProvider } from "@/contexts";
import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { VolunteerRoleEditDrawer } from "@/components/features/VolunteerRoles";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

export interface VolunteerRoleSelectProps {
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  error?: React.ReactNode;
  /** Snapshot label to show for a role that isn't in the loaded list yet. */
  fallbackLabel?: string | null;
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** The picked role's record once known (null when none / not loaded) — e.g. to show its hourly rate. */
  onRoleResolved?: (role: VolunteerRole | null) => void;
}

const VolunteerRoleSelectInner: React.FC<VolunteerRoleSelectProps> = ({
  value,
  onChange,
  disabled,
  error,
  fallbackLabel,
  label,
  description,
  onRoleResolved,
}) => {
  const [roles, setRoles] = useState<VolunteerRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // #142 — may this person add one here? (never for a crew member: it would land in their OWN account).
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);

  const load = async () => {
    setLoading(true);
    const result = await VolunteerRoleService.listAll({ status: [STATUSES.ACTIVE] }, [{ name: "name", order: "ASC" }]);

    if (result.didSucceed()) {
      setRoles((result.data as VolunteerRole[]) || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  /**
   * ⚠ FETCH A ROLE THE ACTIVE LIST DOES NOT HAVE (found 2026-09-19). The list is `status: [ACTIVE]`, so a shift
   * whose role has since been DISABLED fell through to the literal text "Selected role" — and `onRoleResolved`
   * fired with null, which also dropped the hourly-rate hint beneath the Pay field even on a paid role. The
   * host could not tell which role the shift was for, or that it paid.
   */
  useEffect(() => {
    if (!value || loading || roles.some((r) => r.id === value)) {
      return;
    }

    let cancelled = false;

    VolunteerRoleService.get(value).then((result) => {
      const found = result.didSucceed() ? ((result.data as VolunteerRole[]) || [])[0] : null;

      if (!cancelled && found?.id) {
        setRoles((prev) => (prev.some((r) => r.id === found.id) ? prev : [...prev, found]));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [value, loading, roles]);

  const onRoleResolvedRef = useRef(onRoleResolved);
  onRoleResolvedRef.current = onRoleResolved;

  useEffect(() => {
    onRoleResolvedRef.current?.((value && roles.find((r) => r.id === value)) || null);
  }, [roles, value]);

  const data = useMemo(() => {
    const items = roles
      .filter((r) => !!r.id)
      .map((r) => ({ value: r.id as string, label: r.name || "Untitled" }));

    if (value && !items.some((o) => o.value === value)) {
      items.unshift({ value, label: fallbackLabel || "A role that is no longer in your library" });
    }

    return items;
  }, [roles, value, fallbackLabel]);

  const handleCreated = (role: VolunteerRole) => {
    setDrawerOpen(false);
    setRoles((prev) => [role, ...prev]);
    if (role.id) {
      onChange(role.id);
    }
  };

  return (
    <>
      <SelectField
        label={label}
        description={description}
        placeholder={loading ? "Loading…" : "Pick a role (optional)"}
        data={data}
        value={value}
        onChange={(_n, id) => onChange(id)}
        createLabel={canCreate ? "Add a new role" : undefined}
        onCreate={canCreate ? () => setDrawerOpen(true) : undefined}
        searchable
        clearable
        disabled={disabled}
        error={error}
        nothingFoundMessage="No roles yet — add one"
        leftSection={<AppIcon iconName="Users" size={16} />}
        rightSection={loading ? <Loader size="md" /> : undefined}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
        size="md"
      />

      <VolunteerRoleEditDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} onCreated={handleCreated} />
    </>
  );
};

export const VolunteerRoleSelect: React.FC<VolunteerRoleSelectProps> = (props) => {
  return (
    <VolunteerRolesProvider pageSize={DEFAULT_PAGE_SIZE} autoLoad={false}>
      <VolunteerRoleSelectInner {...props} />
    </VolunteerRolesProvider>
  );
};

export default VolunteerRoleSelect;
